import {
  Box,
  Container,
  Divider,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  Link
} from "@mui/material";

export default function SubscriptionInfo() {
  return (
    <Box width="100%">
      <Container maxWidth="lg" sx={{ pt: 2, pb: 2 }}>
        <Grid container sx={{ p: 2, gap: 2 }}>
          <Grid item xs={12}>
            <Typography
              variant="h3"
              component="h3"
              sx={{ fontWeight: "bold" }}
              gutterBottom
            >
              What is Best Coast Pairings?
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography>
              Best Coast Pairings is the all-in-one platform for competitive
              tabletop and TCG gaming events. Whether you're a player attending
              tournaments or a tournament organizer, BCP helps simplify
              everything from registration, list submissions, pairings, scores,
              and historical data. Our mission is to make competitive events
              easier, and facilitate the smoothest tournament experience
              possible.
            </Typography>
          </Grid>

          <Divider sx={{ width: "100%", my: 2 }} />

          <Grid item xs={12}>
            <Typography
              variant="h4"
              component="h4"
              sx={{ fontWeight: "bold" }}
              gutterBottom
            >
              How to Get a Free 1-Month Trial of a Best Coast Pairings
              Subscription
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography gutterBottom>
              We're excited to offer a free one-month trial of a subscription to
              Best Coast Pairings — here's how it works:
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <List sx={{ pl: 2 }}>
              <ListItem
                sx={{ display: "list-item", listStyleType: "decimal", pl: 1 }}
              >
                <ListItemText>
                  Create a Best Coast Pairings account at:
                  <Link
                    href="https://bestcoastpairings.com/login"
                    target="_blank"
                    sx={{ ml: 1 }}
                  >
                    https://bestcoastpairings.com/login
                  </Link>
                </ListItemText>
              </ListItem>

              <ListItem
                sx={{ display: "list-item", listStyleType: "decimal", pl: 1 }}
              >
                <ListItemText>
                  Link your MyWarhammer (Games Workshop) account to your BCP
                  account by navigating to
                  <Link href="/account" sx={{ ml: 1 }}>
                    your account page
                  </Link>
                  , once you're logged in
                </ListItemText>
              </ListItem>

              <ListItem
                sx={{ display: "list-item", listStyleType: "decimal", pl: 1 }}
              >
                <ListItemText>
                  After your accounts are linked, if you're{" "}
                  <em>not yet subscribed</em>, simply
                  <Link href="/subscription" sx={{ ml: 1 }}>
                    subscribe
                  </Link>{" "}
                  and your first month will be <strong>free</strong> — handled
                  at web checkout, no coupon code, no special link required.
                </ListItemText>
              </ListItem>

              <ListItem
                sx={{ display: "list-item", listStyleType: "decimal", pl: 1 }}
              >
                <ListItemText>
                  Enjoy full subscriber access for that month — then decide if
                  you'd like to continue!
                </ListItemText>
              </ListItem>

              <ListItem
                sx={{ display: "list-item", listStyleType: "decimal", pl: 1 }}
              >
                <ListItemText>
                  Want to keep going? Simply maintain your subscription and keep
                  accessing all the benefits.
                </ListItemText>
              </ListItem>
            </List>
          </Grid>

          <Divider sx={{ width: "100%", my: 2 }} />

          <Grid item xs={12}>
            <Typography
              variant="h4"
              component="h4"
              sx={{ fontWeight: "bold" }}
              gutterBottom
            >
              What Services Do We Offer to Players?
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography gutterBottom>
              When you create a FREE Best Coast Pairings account, here's what
              you can do:
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <List sx={{ pl: 2 }}>
              <ListItem
                sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
              >
                <ListItemText>
                  Find and register for events in your area or globally.
                </ListItemText>
              </ListItem>

              <ListItem
                sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
              >
                <ListItemText>
                  Upload your army list or deck list ahead of time
                </ListItemText>
              </ListItem>

              <ListItem
                sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
              >
                <ListItemText>Check-in to the event remotely</ListItemText>
              </ListItem>

              <ListItem
                sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
              >
                <ListItemText>
                  View pairings, placings, and results during events for which
                  you are registered.
                </ListItemText>
              </ListItem>

              <ListItem
                sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
              >
                <ListItemText>
                  Receive real-time updates and stay connected with the event
                  beyond just being a competitor.
                </ListItemText>
              </ListItem>

              <ListItem
                sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
              >
                <ListItemText>
                  Event Hosting: Easily create, manage, and host tournaments of
                  any size.
                </ListItemText>
              </ListItem>

              <ListItem
                sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
              >
                <ListItemText>
                  BCP Organizations allows you to host casual events, campaigns,
                  and game nights — promote your store on our event map and take
                  payments directly through Square.
                </ListItemText>
              </ListItem>
            </List>
          </Grid>

          <Divider sx={{ width: "100%", my: 2 }} />

          <Grid item xs={12}>
            <Typography
              variant="h4"
              component="h4"
              sx={{ fontWeight: "bold" }}
              gutterBottom
            >
              Subscriber Benefits: What You Get When You Upgrade
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography gutterBottom>
              Upgrading to a BCP subscription unlocks advanced features designed
              for serious players and hobby-enthusiasts:
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <List sx={{ pl: 2 }}>
              <ListItem
                sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
              >
                <ListItemText>
                  <strong>
                    Unlimited access to all submitted lists globally
                  </strong>{" "}
                  — every round, every pairing, every placing for every game
                  system.
                </ListItemText>
              </ListItem>

              <ListItem
                sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
              >
                <ListItemText>
                  <strong>Advanced insights & rewards (where available)</strong>{" "}
                  Up-to-date competitive data, community rankings, and priority
                  resources for event participants. Dig deep into data for
                  preparation, analysis, or simply browsing top-tables.
                </ListItemText>
              </ListItem>

              <ListItem
                sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
              >
                <ListItemText>
                  Follow any event that is happening even if you aren't
                  registered. See pairings, placings, lists, and results for any
                  event anywhere!
                </ListItemText>
              </ListItem>

              <ListItem
                sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
              >
                <ListItemText>
                  Subscribers help support the infrastructure and growth of the
                  events ecosystem — you're part of making organized play
                  better.
                </ListItemText>
              </ListItem>
            </List>
          </Grid>

          <Divider sx={{ width: "100%", my: 2 }} />

          <Grid item xs={12}>
            <Typography
              variant="h4"
              component="h4"
              sx={{ fontWeight: "bold" }}
              gutterBottom
            >
              Why Link MyWarhammer and Why This Trial?
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography gutterBottom>
              By linking your MyWarhammer account with BCP, you're helping
              streamline the entire tournament experience:
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <List sx={{ pl: 2 }}>
              <ListItem
                sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
              >
                <ListItemText>
                  Registration becomes quicker and smoother.
                </ListItemText>
              </ListItem>

              <ListItem
                sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
              >
                <ListItemText>
                  Player identities are verified, so standings and rankings are
                  accurate.
                </ListItemText>
              </ListItem>

              <ListItem
                sx={{ display: "list-item", listStyleType: "disc", pl: 1 }}
              >
                <ListItemText>
                  Your tournament history stays updated and correct.
                </ListItemText>
              </ListItem>
            </List>
          </Grid>

          <Divider sx={{ width: "100%", my: 2 }} />

          <Grid item xs={12}>
            <Typography gutterBottom>
              Thanks for being a part of the Best Coast Pairings community.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography>See you at the tables!</Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
